import React, { useId } from 'react';
import { Menu } from 'lucide-react';
import { Reorder, useDragControls, useReducedMotion } from 'motion/react';
import { ADMIN_TAB_LABELS, moveAdminTab, type AdminTabId } from '../../lib/admin-tab-order';

export function AdminTabOrderList({ order, onReorder, disabled }: {
  order: AdminTabId[];
  onReorder: (order: AdminTabId[]) => void;
  disabled: boolean;
}) {
  const instructions = useId();
  return (
    <div>
      <p id={instructions} className="sr-only">Hold the three-line handle and drag. With a keyboard, focus the handle and use the Up and Down arrow keys.</p>
      <Reorder.Group axis="y" values={order} onReorder={onReorder} className="space-y-1">
        {order.map((id, index) => <TabItem key={id} id={id} position={index + 1} disabled={disabled} instructions={instructions} onMove={direction => onReorder(moveAdminTab(order, id, direction))} />)}
      </Reorder.Group>
    </div>
  );
}

function TabItem({ id, position, disabled, instructions, onMove }: {
  id: AdminTabId;
  position: number;
  disabled: boolean;
  instructions: string;
  onMove: (direction: 'up' | 'down') => void;
}) {
  const controls = useDragControls();
  const reducedMotion = useReducedMotion();
  return (
    <Reorder.Item value={id} drag={disabled ? false : 'y'} dragListener={false} dragControls={controls}
      data-tab-order-id={id} transition={reducedMotion ? { duration: 0 } : undefined}
      whileDrag={{ zIndex: 1, boxShadow: '0 6px 18px rgb(0 0 0 / 0.16)' }}
      className="relative flex items-center gap-3 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-1)] px-2 py-1">
      <button type="button" data-tab-drag-handle={id} aria-label={`Drag ${ADMIN_TAB_LABELS[id]} to reorder`}
        aria-describedby={instructions} aria-keyshortcuts="ArrowUp ArrowDown" disabled={disabled}
        className="flex h-11 w-11 shrink-0 cursor-grab items-center justify-center rounded-[var(--radius-md)] border border-[var(--color-border)] text-[var(--color-text-secondary)] active:cursor-grabbing disabled:cursor-wait disabled:opacity-40"
        style={{ touchAction: 'none' }}
        onPointerDown={event => {
          if (!disabled && event.isPrimary && event.button === 0) {
            event.currentTarget.focus({ preventScroll: true });
            controls.start(event);
          }
        }}
        onKeyDown={event => {
          if (!disabled && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
            event.preventDefault();
            onMove(event.key === 'ArrowUp' ? 'up' : 'down');
          }
        }}>
        <Menu aria-hidden="true" className="h-5 w-5" />
      </button>
      <span className="min-w-0 text-sm">{position}. {ADMIN_TAB_LABELS[id]}</span>
    </Reorder.Item>
  );
}
